import { isNumberVariant, normalizeTag } from './tag-variants';

/**
 * What a product is (its TYPE) and what it is for (its PURPOSE): two fixed
 * lists, owner-written on 2026-10-20.
 *
 * A product stores one type (`catalogType`), one main purpose (`purpose`) and
 * at most one more (`secondaryPurpose`, never the same as the main). The
 * listing forms guess them (catalog-taxonomy-inference.ts) and the seller
 * confirms or changes the guess. Nobody adds to these lists: the forms offer
 * only these entries, firestore.rules refuses any other value
 * (catalogTypes() and catalogPurposes(), pinned by catalog-taxonomy.test.ts),
 * and a product saved before the fields existed is read by its guess until its
 * seller next saves it (effectiveTaxonomyOf in product-filters.ts).
 *
 * The marketplace shows every purpose in the bar under its search box, in the
 * order marketplace-purposes.ts decides, Other and Resell rights last. Types
 * are a filter in its Filters menu.
 *
 * TAGS DO NOT REPEAT THEM. A tag that names an entry ("templates", "marketing",
 * "plr") is refused by the tag picker, which offers the field instead, and the
 * importer turns one into the field (taxonomyTagMatch below). Phrases stay
 * tags: "budget-planner" says more than the type does. Tools (notion, canva),
 * formats (svg) and sub-kinds (icons, sound-effects) stay tags too: none of
 * them is an entry here.
 *
 * MIRRORED in functions/src/lib/catalog-taxonomy.ts (slugs and tag aliases, for
 * the importer). catalog-taxonomy.test.ts compares the two and the rules.
 */

export type TaxonomyEntry = {
  /** The stored value, and the `purpose=` / `type=` value in a marketplace URL. */
  slug: string;
  label: string;
  /** What belongs here, shown under the option in the listing form. */
  examples: string;
  /**
   * Tags that name this entry, in tag-key form (see tagKey). Each also covers
   * its other number ("template" covers "templates"); both are listed where
   * the plural is irregular or the word has two parts.
   */
  tagAliases: readonly string[];
  /** The niche page's title when "{label} digital products" reads wrong (niche-pages.ts). */
  nicheTitle?: string;
};

/** The Other entry in both lists, for anything none of the rest fits. */
export const OTHER_SLUG = 'other';

/** The purpose for resale-rights packs (PLR, MRR), always last in the bar. */
export const RESELL_RIGHTS_PURPOSE = 'resell-rights';

export const CATALOG_TYPES: readonly TaxonomyEntry[] = [
  { slug: 'template', label: 'Template', examples: 'Notion, Canva, Sheets, Docs, Figma, Framer and video templates, website themes', tagAliases: ['template', 'templates'] },
  { slug: 'kit', label: 'Kit', examples: 'Several parts in one product, often across tools', tagAliases: ['kit', 'kits'] },
  { slug: 'planner-journal', label: 'Planner & journal', examples: 'PDF and GoodNotes planners, trackers, journals', tagAliases: ['planner', 'planners', 'journal', 'journals', 'planner-journal', 'planner-and-journal', 'planners-and-journals'] },
  { slug: 'printable', label: 'Printable', examples: 'Worksheets, wall art, activity sheets, party printables', tagAliases: ['printable', 'printables'] },
  { slug: 'ebook-guide', label: 'Ebook & guide', examples: 'How-to guides, workbooks, handbooks, RPG adventures', tagAliases: ['ebook', 'ebooks', 'e-book', 'e-books', 'guide', 'guides', 'ebook-guide', 'ebook-and-guide', 'ebooks-and-guides'] },
  { slug: 'course', label: 'Course', examples: 'Video or multi-lesson courses', tagAliases: ['course', 'courses'] },
  { slug: 'graphics', label: 'Graphics', examples: 'Illustrations, icons, clip art, stickers, overlays', tagAliases: ['graphic', 'graphics'] },
  { slug: 'ui-kit', label: 'UI kit', examples: 'Figma design systems and component kits', tagAliases: ['ui-kit', 'ui-kits', 'uikit', 'uikits'] },
  { slug: 'mockup', label: 'Mockup', examples: 'Product, device and apparel mockups', tagAliases: ['mockup', 'mockups', 'mock-up', 'mock-ups'] },
  { slug: 'font', label: 'Font', examples: 'Typefaces and font families', tagAliases: ['font', 'fonts'] },
  { slug: 'brushes-textures', label: 'Brushes & textures', examples: 'Procreate and Photoshop brushes, textures, stamps', tagAliases: ['brush', 'brushes', 'texture', 'textures', 'brushes-textures', 'brushes-and-textures'] },
  { slug: 'presets-luts', label: 'Presets & LUTs', examples: 'Lightroom presets, video LUTs, synth presets', tagAliases: ['preset', 'presets', 'lut', 'luts', 'presets-luts', 'presets-and-luts'] },
  { slug: 'stock-media', label: 'Stock photo & video', examples: 'Photos, footage, backgrounds', tagAliases: ['stock-photo', 'stock-photos', 'stock-video', 'stock-videos', 'stock-footage', 'stock-media', 'stock-photo-and-video'] },
  { slug: 'audio', label: 'Audio', examples: 'Samples, loops, sound effects, stock music', tagAliases: ['audio'] },
  { slug: 'sheet-music', label: 'Sheet music', examples: 'Scores, arrangements, exercises', tagAliases: ['sheet-music'] },
  // No tag alias: "pattern" is as often a seamless pattern (Graphics), so it
  // stays a tag rather than shelving a surface designer under sewing.
  { slug: 'pattern', label: 'Pattern', examples: 'Sewing, knitting, crochet and cross-stitch patterns', tagAliases: [] },
  { slug: 'cut-file', label: 'Cut file', examples: 'SVG and DXF files for Cricut, Silhouette and lasers', tagAliases: ['cut-file', 'cut-files'] },
  { slug: 'embroidery-file', label: 'Embroidery file', examples: 'Machine embroidery designs', tagAliases: ['embroidery-file', 'embroidery-files'] },
  { slug: '3d-model', label: '3D model', examples: 'Printable models, game models, avatars', tagAliases: ['3d-model', '3d-models'] },
  { slug: 'game-asset', label: 'Game asset', examples: 'Sprites, tilesets, game-ready packs', tagAliases: ['game-asset', 'game-assets'] },
  { slug: 'code-plugin', label: 'Code & plugin', examples: 'Scripts, plugins, starter kits', tagAliases: ['code', 'plugin', 'plugins', 'code-plugin', 'code-and-plugin', 'code-and-plugins'] },
  { slug: 'workflow', label: 'Workflow', examples: 'n8n, Make and Zapier automations', tagAliases: ['workflow', 'workflows'] },
  { slug: OTHER_SLUG, label: 'Other', examples: 'Anything the types above do not cover', tagAliases: ['other'] },
];

export const CATALOG_PURPOSES: readonly TaxonomyEntry[] = [
  // Named Teaching (owner, 2026-10-27; slug kept, so every stored value and
  // link still works): it is for resources teachers, tutors and homeschooling
  // parents use with students, and "Education" drew guides that teach adults a
  // skill. The marketplace shows it only on a product with a year level
  // (effectiveTaxonomyOf in product-filters.ts).
  {
    slug: 'education', label: 'Teaching',
    examples: 'Lesson plans, worksheets and classroom resources for teachers, tutors and homeschool',
    tagAliases: ['education', 'educational'], nicheTitle: 'Teaching resources',
  },
  { slug: 'business', label: 'Business', examples: 'Pitch decks, business plans, SOPs, contracts, practice admin, client kits', tagAliases: ['business', 'businesses'] },
  { slug: 'marketing-sales', label: 'Marketing & sales', examples: 'Social media, branding, listing presentations, ads, email', tagAliases: ['marketing', 'sales', 'marketing-sales', 'marketing-and-sales'] },
  { slug: 'finance', label: 'Finance', examples: 'Budgets, bookkeeping, financial models, investment trackers', tagAliases: ['finance', 'finances', 'financial'] },
  { slug: 'productivity', label: 'Productivity', examples: 'Planners, trackers, Notion systems, organisation', tagAliases: ['productivity'] },
  { slug: 'careers', label: 'Careers', examples: 'Resumes, cover letters, interview prep, portfolios', tagAliases: ['career', 'careers'] },
  { slug: 'ai-automation', label: 'AI & automation', examples: 'n8n, Make and Zapier workflows, AI agent templates', tagAliases: ['automation', 'automations', 'ai-automation', 'ai-and-automation'] },
  { slug: 'software-web', label: 'Software & web', examples: 'Website themes, code, starter kits, plugins, no-code templates', tagAliases: ['software', 'software-web', 'software-and-web'] },
  { slug: 'art-design', label: 'Art & design', examples: 'Graphics, illustrations, icons, UI kits, mockups, brushes, fonts', tagAliases: ['art', 'arts', 'design', 'designs', 'art-design', 'art-and-design'] },
  { slug: 'photo-video', label: 'Photo & video', examples: 'Presets, LUTs, editing templates, stock footage', tagAliases: ['photo-video', 'photo-and-video', 'photos-and-videos'] },
  { slug: 'music-audio', label: 'Music & audio', examples: 'Samples, synth presets, sound effects, stock music, sheet music', tagAliases: ['music', 'music-audio', 'music-and-audio'] },
  { slug: 'games', label: 'Games', examples: 'Game dev assets, tabletop RPG, game guides', tagAliases: ['game', 'games', 'gaming'] },
  { slug: 'content-creation', label: 'Content creation', examples: 'Stream overlays, emotes, thumbnails, YouTube and podcast kits', tagAliases: ['content-creation'] },
  { slug: 'crafts', label: 'Crafts', examples: 'Sewing, knitting, crochet, embroidery, cutting and laser files, 3D printing', tagAliases: ['craft', 'crafts', 'crafting'] },
  { slug: 'home-family', label: 'Home & family', examples: 'Household planning, parenting, kids\' activities, wall art', tagAliases: ['home-family', 'home-and-family'] },
  { slug: 'health-wellbeing', label: 'Health & wellbeing', examples: 'Fitness programmes, mental health and therapy tools, wellness', tagAliases: ['health', 'wellbeing', 'well-being', 'wellness', 'health-wellbeing', 'health-and-wellbeing'] },
  { slug: 'food-drink', label: 'Food & drink', examples: 'Recipes, meal plans, cookbooks', tagAliases: ['food-drink', 'food-and-drink'] },
  { slug: 'travel', label: 'Travel', examples: 'Guides, itineraries, maps', tagAliases: ['travel'] },
  { slug: 'events-celebrations', label: 'Events & celebrations', examples: 'Weddings, parties, invitations, holidays', tagAliases: ['event', 'events', 'celebration', 'celebrations', 'events-celebrations', 'events-and-celebrations'] },
  { slug: 'personal-growth', label: 'Personal growth', examples: 'Journals, self-help, mindfulness, faith', tagAliases: ['personal-growth'] },
  { slug: 'writing-publishing', label: 'Writing & publishing', examples: 'Book templates, author tools, writing guides', tagAliases: ['writing', 'publishing', 'writing-publishing', 'writing-and-publishing'] },
  { slug: OTHER_SLUG, label: 'Other', examples: 'Anything the purposes above do not cover', tagAliases: [] },
  { slug: RESELL_RIGHTS_PURPOSE, label: 'Resell rights', examples: 'PLR and MRR packs that buyers may resell or rebrand', tagAliases: ['resell-rights', 'resale-rights', 'plr', 'mrr', 'plr-mrr', 'private-label-rights', 'master-resell-rights', 'resell', 'resale'] },
];

export const CATALOG_TYPE_SLUGS: readonly string[] = CATALOG_TYPES.map((t) => t.slug);
export const CATALOG_PURPOSE_SLUGS: readonly string[] = CATALOG_PURPOSES.map((p) => p.slug);

const TYPE_BY_SLUG = new Map(CATALOG_TYPES.map((t) => [t.slug, t]));
const PURPOSE_BY_SLUG = new Map(CATALOG_PURPOSES.map((p) => [p.slug, p]));

export function isCatalogType(value: unknown): value is string {
  return typeof value === 'string' && TYPE_BY_SLUG.has(value);
}

export function isCatalogPurpose(value: unknown): value is string {
  return typeof value === 'string' && PURPOSE_BY_SLUG.has(value);
}

export function catalogTypeLabel(slug: unknown): string | null {
  return typeof slug === 'string' ? TYPE_BY_SLUG.get(slug)?.label ?? null : null;
}

export function catalogPurposeLabel(slug: unknown): string | null {
  return typeof slug === 'string' ? PURPOSE_BY_SLUG.get(slug)?.label ?? null : null;
}

export function catalogTypeEntry(slug: unknown): TaxonomyEntry | null {
  return typeof slug === 'string' ? TYPE_BY_SLUG.get(slug) ?? null : null;
}

export function catalogPurposeEntry(slug: unknown): TaxonomyEntry | null {
  return typeof slug === 'string' ? PURPOSE_BY_SLUG.get(slug) ?? null : null;
}

/**
 * A tag reduced for comparing: the picker's normal form (normalizeTag), then
 * every run of anything but a letter or a digit one hyphen, trimmed. So
 * "Marketing & Sales", "marketing-&-sales" and "marketing sales" are all
 * "marketing-sales", the way tagSlug reduces a tag for its page.
 */
function tagKey(tag: string): string {
  return normalizeTag(tag).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export type TaxonomyTagMatch = {
  kind: 'type' | 'purpose';
  slug: string;
  label: string;
};

/** The listing forms' question for each field, which the tag picker's notices name too. */
export const TAXONOMY_FIELD_QUESTIONS: Readonly<Record<TaxonomyTagMatch['kind'], string>> = {
  type: 'What is it?',
  purpose: `What's it for?`,
};

/**
 * Aliases matched only as written, never in their other number (the rest match
 * either, isNumberVariant): "sale" is a discount, not Marketing & sales.
 * MIRRORED as EXACT_TAG_ALIASES in functions/src/lib/catalog-taxonomy.ts.
 */
export const EXACT_TAG_ALIASES: ReadonlySet<string> = new Set(['sales']);

/**
 * The longest a tag can be and still name an entry: the longest alias in its
 * plural, with room for spaces and punctuation. A longer string is answered at
 * once, without the memo or the scan, so a request cannot grow the memo with a
 * kilobyte per entry (the middleware and the marketplace's ?tags= read
 * whatever an address carries). Mirrored with the same value.
 */
export const TAG_MATCH_MAX_LENGTH = 48;

/**
 * The type or purpose a tag names, if it names one: the tag's key is one of an
 * entry's tagAliases, or that alias in its other number (isNumberVariant, the
 * picker's own rule) unless it is an exact-only alias. Null for anything else,
 * phrases included.
 *
 * Remembered per tag: the marketplace's guess asks about every tag of every
 * product (catalog-taxonomy-inference.ts), a catalogue has few distinct tags,
 * and one answer costs a pass over every alias. The answers are frozen, since
 * callers share them.
 */
export function taxonomyTagMatch(tag: string): TaxonomyTagMatch | null {
  const raw = typeof tag === 'string' ? tag : '';
  if (raw.length > TAG_MATCH_MAX_LENGTH) return null;
  const known = tagMatchMemo.get(raw);
  if (known !== undefined) return known;
  const match = matchTaxonomyTag(raw);
  if (tagMatchMemo.size >= TAG_MATCH_MEMO_MAX) tagMatchMemo.clear();
  tagMatchMemo.set(raw, match);
  return match;
}

const TAG_MATCH_MEMO_MAX = 10_000;
const tagMatchMemo = new Map<string, TaxonomyTagMatch | null>();

/**
 * Where a retired tag page goes. A word that names a type or purpose is not a
 * tag any more, so /tag/{word} (indexed, and linked from older product pages)
 * opens the marketplace's view of that type or purpose instead
 * (src/middleware.ts redirects there, permanently). The address's own query
 * (a campaign's utm, a referral) rides along. Null for any other path, a path
 * that does not decode included.
 *
 * @param search The address's query string, with or without its "?".
 * @returns A path and query on this site.
 */
export function taxonomyTagPageRedirect(pathname: string, search = ''): string | null {
  const segment = /^\/tag\/([^/]+)\/?$/.exec(pathname)?.[1];
  if (!segment) return null;
  let word: string;
  try {
    word = decodeURIComponent(segment);
  } catch {
    return null;
  }
  const match = taxonomyTagMatch(word);
  if (!match) return null;
  const query = new URLSearchParams({ [match.kind === 'type' ? 'type' : 'purpose']: match.slug });
  for (const [key, value] of new URLSearchParams(search)) {
    if (!query.has(key)) query.append(key, value);
  }
  return `/marketplace?${query.toString()}`;
}

function matchTaxonomyTag(tag: string): TaxonomyTagMatch | null {
  const key = tagKey(tag);
  if (!key) return null;
  const lists: ReadonlyArray<readonly [TaxonomyTagMatch['kind'], readonly TaxonomyEntry[]]> = [
    ['type', CATALOG_TYPES],
    ['purpose', CATALOG_PURPOSES],
  ];
  for (const [kind, entries] of lists) {
    for (const entry of entries) {
      if (entry.tagAliases.some((alias) => alias === key || (!EXACT_TAG_ALIASES.has(alias) && isNumberVariant(alias, key)))) {
        return Object.freeze({ kind, slug: entry.slug, label: entry.label });
      }
    }
  }
  return null;
}

/**
 * A tag list without the tags that name a type or purpose, in order, and what
 * each removed tag named. The forms strip them when a product is opened and
 * show the seller where they went.
 */
export function withoutTaxonomyTags(
  tags: readonly string[],
): { tags: string[]; matches: Array<TaxonomyTagMatch & { tag: string }> } {
  const kept: string[] = [];
  const matches: Array<TaxonomyTagMatch & { tag: string }> = [];
  for (const tag of tags) {
    const match = typeof tag === 'string' ? taxonomyTagMatch(tag) : null;
    if (match) matches.push({ ...match, tag });
    else kept.push(tag);
  }
  return { tags: kept, matches };
}
